import THREE from 'three';

import {
  TransformGizmoRotate,
  TransformGizmoScale,
  TransformGizmoTranslate,
  TransformGizmoTranslateRotate } from
'./TransformGizmos';


const _pointerVector = new THREE.Vector3();
const _pointerDir = new THREE.Vector3();
const _ray = new THREE.Raycaster();

// Load viewer-shipped textures as an anonymous CORS request, bypassing the global THREE.ImageUtils.crossOrigin flag
// that TextureLoader switches to 'use-credentials' for authenticated model sessions. Without this, the gizmo PNGs get
// tainted at upload time, texImage2D throws SecurityError every frame, and rotation loops forever.
function loadGizmoTexture(url) {
  const texture = new THREE.Texture();

  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => {texture.image = image;texture.needsUpdate = true;};

  image.src = url;
  return texture;
}


export class TransformControls extends THREE.Object3D {
  constructor(camera, domElement, mode, includeAxis) {

    // TODO: Make non-uniform scale and rotate play nice in hierarchies
    // TODO: ADD RXYZ contol

    super();

    this._camera = camera;
    this._domElement = domElement !== undefined ? domElement : document;

    this.gizmo = {};
    switch (mode) {
      case "translate":
        this.gizmo[mode] = new TransformGizmoTranslate(includeAxis);
        break;
      case "rotate":
        this.gizmo[mode] = new TransformGizmoRotate(includeAxis);
        break;
      case "transrotate":
        this.gizmo[mode] = new TransformGizmoTranslateRotate(includeAxis);
        break;
      case "scale":
        this.gizmo[mode] = new TransformGizmoScale(includeAxis);
        break;
    }

    this.add(this.gizmo[mode]);
    this.gizmo[mode].hide();

    this.object = undefined;
    this.snap = null;
    this.snapDelta = 0;
    this.space = "world";
    this.size = 1;
    this.axis = null;
    this.useAllPickers = true;

    this.unitX = new THREE.Vector3(1, 0, 0);
    this.unitY = new THREE.Vector3(0, 1, 0);
    this.unitZ = new THREE.Vector3(0, 0, 1);
    this.normal = new THREE.Vector3(0, 0, 1);

    if (mode === "transrotate") {
      let vertices = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 1)];
      let geometry = new THREE.BufferGeometry().setFromPoints(vertices);
      let material = new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 2, depthTest: false });
      this.startLine = new THREE.Line(geometry, material);

      vertices = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 1)];
      geometry = new THREE.BufferGeometry().setFromPoints(vertices);
      material = new THREE.LineBasicMaterial({ color: 0xffe603, linewidth: 2, depthTest: false });
      this.endLine = new THREE.Line(geometry, material);

      vertices = [new THREE.Vector3(0, -1, 0), new THREE.Vector3(0, 1, 0)];
      geometry = new THREE.BufferGeometry().setFromPoints(vertices);
      material = new THREE.LineDashedMaterial({ color: 0x000000, linewidth: 1, depthTest: false });
      this.centerLine = new THREE.Line(geometry, material);

      let map = loadGizmoTexture(Autodesk.Viewing.Private.getResourceUrl("res/textures/centerMarker_X.png"));
      map.magFilter = map.minFilter = THREE.NearestFilter;
      geometry = new THREE.CircleBufferGeometry(0.1, 32);
      material = new THREE.MeshBasicMaterial({ opacity: 1, side: THREE.DoubleSide, transparent: true, map: map });
      this.centerMark = new THREE.Mesh(geometry, material);
      this.centerMark.rotation.set(Math.PI / 2, 0, 0);

      this.ticks = {};
      map = loadGizmoTexture(Autodesk.Viewing.Private.getResourceUrl("res/textures/cardinalPoint.png"));
      map.magFilter = map.minFilter = THREE.NearestFilter;
      material = new THREE.MeshBasicMaterial({ depthTest: false, opacity: 1, transparent: true, side: THREE.DoubleSide, map: map });
      let w = 0.12,h = 0.25,d = 1.15;

      this.ticks["RX"] = new THREE.Object3D();
      geometry = new THREE.PlaneBufferGeometry(w, h);
      let mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(0, 0, -d - h / 2);
      mesh.rotation.set(Math.PI / 2, Math.PI / 2, 0);
      this.ticks["RX"].add(mesh);

      mesh = mesh.clone();
      mesh.position.set(0, d + h / 2, 0);
      mesh.rotation.set(0, Math.PI / 2, 0);
      this.ticks["RX"].add(mesh);

      mesh = mesh.clone();
      mesh.position.set(0, 0, d + h / 2);
      mesh.rotation.set(0, Math.PI / 2, Math.PI / 2);
      this.ticks["RX"].add(mesh);

      mesh = mesh.clone();
      mesh.position.set(0, -d - h / 2, 0);
      mesh.rotation.set(0, Math.PI / 2, 0);
      this.ticks["RX"].add(mesh);

      this.ticks["RY"] = new THREE.Object3D();
      mesh = mesh.clone();
      mesh.position.set(0, 0, -d - h / 2);
      mesh.rotation.set(Math.PI / 2, 0, 0);
      this.ticks["RY"].add(mesh);

      mesh = mesh.clone();
      mesh.position.set(-d - h / 2, 0, 0);
      mesh.rotation.set(Math.PI / 2, 0, Math.PI / 2);
      this.ticks["RY"].add(mesh);

      mesh = mesh.clone();
      mesh.position.set(0, 0, d + h / 2);
      mesh.rotation.set(Math.PI / 2, 0, 0);
      this.ticks["RY"].add(mesh);

      mesh = mesh.clone();
      mesh.position.set(d + h / 2, 0, 0);
      mesh.rotation.set(Math.PI / 2, 0, Math.PI / 2);
      this.ticks["RY"].add(mesh);
    }

    this._dragging = false;
    this._mode = mode;

    this._changeEvent = { type: "change" };
    this._mouseDownEvent = { type: "mouseDown" };
    this._mouseUpEvent = { type: "mouseUp", mode: this._mode };
    this._objectChangeEvent = { type: "objectChange" };

    this._point = new THREE.Vector3();
    this._offset = new THREE.Vector3();

    this._rotation = new THREE.Vector3();
    this._offsetRotation = new THREE.Vector3();
    this._scale = 1;
    this.clientScale = 1;

    this._lookAtMatrix = new THREE.Matrix4();
    this._eye = new THREE.Vector3();

    this._tempMatrix = new THREE.Matrix4();
    this._tempVector = new THREE.Vector3();
    this._tempQuaternion = new THREE.Quaternion();
    this._projX = new THREE.Vector3();
    this._projY = new THREE.Vector3();
    this._projZ = new THREE.Vector3();

    this._quaternionXYZ = new THREE.Quaternion();
    this._quaternionX = new THREE.Quaternion();
    this._quaternionY = new THREE.Quaternion();
    this._quaternionZ = new THREE.Quaternion();
    this._quaternionE = new THREE.Quaternion();

    this._oldPosition = new THREE.Vector3();
    this._oldScale = new THREE.Vector3();
    this._oldRotationMatrix = new THREE.Matrix4();

    this._parentRotationMatrix = new THREE.Matrix4();
    this._parentScale = new THREE.Vector3();

    this._worldPosition = new THREE.Vector3();
    this._worldRotation = new THREE.Euler();
    this._worldRotationMatrix = new THREE.Matrix4();
    this._camPosition = new THREE.Vector3();
  }

  _intersectObjects(pointer, objects) {
    return TransformControls.intersectObjects(pointer.canvasX, pointer.canvasY, objects, this._camera, true);
  }

  attach(object) {

    this.object = object;

    this.gizmo[this._mode].show();

    this.update();

    this.updateUnitVectors();

  }

  detach(object) {

    this.object = undefined;
    this.axis = null;

    this.gizmo[this._mode].hide();

  }

  setMode(mode) {

    this._mode = mode ? mode : this._mode;

    if (this._mode == "scale") this.space = "local";

    this.gizmo[this._mode].show();

    this.update();
    this.dispatchEvent(this._changeEvent);

  }

  getPicker() {

    return this.gizmo[this._mode].hemiPicker.children;

  }

  setPosition(position) {

    this.object.position.copy(position);
    this.update();

  }

  setNormal(normal) {

    this._tempQuaternion.setFromUnitVectors(this.normal, normal);
    this.unitX.applyQuaternion(this._tempQuaternion);
    this.unitY.applyQuaternion(this._tempQuaternion);
    this.unitZ.applyQuaternion(this._tempQuaternion);
    this.normal.copy(normal);
    if (this.object) {
      this.object.quaternion.multiply(this._tempQuaternion);
    }
    this.update();
  }

  setRotation(rotationMatrix) {
    if (this.object) {
      this.object.quaternion.setFromRotationMatrix(rotationMatrix);
      this.update();
      this.updateUnitVectors();
    }
  }

  setSnap(snap, delta) {

    this.snap = snap;
    this.snapDelta = delta;

  }

  setSize(size) {

    this.size = size;
    this.update();
    this.dispatchEvent(this._changeEvent);

  }

  setSpace(space) {

    this.space = space;
    this.update();
    this.dispatchEvent(this._changeEvent);

  }

  update(highlight) {

    if (this.object === undefined) return;

    this.object.updateMatrixWorld();
    this._worldPosition.setFromMatrixPosition(this.object.matrixWorld);
    this._worldRotation.setFromRotationMatrix(this._tempMatrix.extractRotation(this.object.matrixWorld));

    this._camera.updateMatrixWorld();
    this._camPosition.setFromMatrixPosition(this._camera.matrixWorld);

    this.position.copy(this._worldPosition);
    //camRotation.setFromRotationMatrix( tempMatrix.extractRotation( camera.matrixWorld ) );

    // Set the gizmo position with the specified offset.
    if (this.gizmoOffset) {
      this.position.add(this.gizmoOffset);
    }
    this.updateMatrixWorld();

    this.quaternion.setFromEuler(this._worldRotation);

    this.normal.set(0, 0, 1);
    this.normal.applyEuler(this._worldRotation);

    // keep same screen height (100px)
    var height;
    if (this._camera.isPerspective) {
      var dist = this.position.distanceTo(this._camPosition);
      height = 2 * Math.tan(this._camera.fov * Math.PI / 360) * dist;
    } else {
      // orthographic, so the world height is simply top minus bottom
      height = this._camera.top - this._camera.bottom;
    }
    var rect = this._domElement.getBoundingClientRect();
    // multiply 100 pixels by world height for the window, divide by window height in pixels,
    // to get world height equivalent to 100 pixels.
    this._scale = this.clientScale * 100 * height / rect.height;
    this.scale.set(this._scale, this._scale, this._scale);

    //eye.copy( camPosition ).sub( worldPosition ).normalize();

    //if ( scope.space == "local" )
    //    this.gizmo[_mode].update( worldRotation, eye );
    //else if ( scope.space == "world" )
    //    this.gizmo[_mode].update( new THREE.Euler(), eye );

    if (highlight)
    this.gizmo[this._mode].highlight(this.axis);

  }

  setGizmoOffset(vec) {
    // Reset the gizmo if no vector passed in.
    this.gizmoOffset = !vec ? new THREE.Vector3(0, 0, 0) : vec;
    this.update();
  }

  updateUnitVectors() {

    this.unitX.set(1, 0, 0);
    this.unitY.set(0, 1, 0);
    this.unitZ.set(0, 0, 1);
    this.unitX.applyEuler(this._worldRotation);
    this.unitY.applyEuler(this._worldRotation);
    this.unitZ.applyEuler(this._worldRotation);

  }

  showRotationGizmos(set) {

    var handles = this.gizmo[this._mode].handles.children;
    for (var i = 0; i < handles.length; i++) {
      var child = handles[i];
      child.visible = true;
      if (child.name.search("R") !== -1) child.visible = set;
    }
    this.useAllPickers = set;

  }

  highlight() {

    this.gizmo[this._mode].highlight(this.axis || "Z");

  }

  onPointerHover(event) {

    if (this.object === undefined || this._dragging === true) return false;

    var pointer = event;

    var intersect = this._intersectObjects(pointer, this.useAllPickers ? this.gizmo[this._mode].pickers.children : this.gizmo[this._mode].subPickers.children);

    var axis = null;
    var mode = "";

    if (intersect) {

      axis = intersect.object.name;
      mode = axis.search("R") != -1 ? "rotate" : "translate";

    }

    if (this.axis !== axis) {

      this.axis = axis;
      this.gizmo[this._mode].activeMode = mode;
      this.update(true);
      this.dispatchEvent(this._changeEvent);

    }

    if (this.axis === null) {

      this.gizmo[this._mode].show();

    }

    return intersect ? true : false;

  }

  isDragging() {
    return this._dragging;
  }

  onPointerDown(event) {

    if (this.object === undefined || this._dragging === true) return false;

    var pointer = event;

    if (event.pointerType === 'touch') {

      let intersect = this._intersectObjects(pointer, this.useAllPickers ? this.gizmo[this._mode].pickers.children : this.gizmo[this._mode].subPickers.children);

      var axis = null;
      var mode = "";

      if (intersect) {

        axis = intersect.object.name;
        mode = axis.search("R") != -1 ? "rotate" : "translate";

      }

      if (this.axis !== axis) {

        this.axis = axis;
        this.gizmo[this._mode].activeMode = mode;
      }
    }

    let intersect = null;

    if (pointer.button === 0 || pointer.button === -1 || pointer.button === undefined) {

      intersect = this._intersectObjects(pointer, this.useAllPickers ? this.gizmo[this._mode].pickers.children : this.gizmo[this._mode].subPickers.children);

      if (intersect) {

        this.dispatchEvent(this._mouseDownEvent);

        this.axis = intersect.object.name;

        this.update();

        this._eye.copy(this._camera.position).sub(this._worldPosition).normalize();

        // The eye vector is used to ensure that we choose a plane that is not parallel to the view direction.
        // When using an orthographic camera, the direction from gizmo to camera (as used above) doesn't matter for this,
        // because the view rays for any pixel is always parallel to the camera world direction.
        //
        // E.g. it may happen that the axis of largest extent in the eye vector above is x, while the ortho-camera direction
        // is actually y. In this case, setAxisPlane() would choose the "YZ"-plane for dragging along z-axis, so that hittests
        // with this plane will not work (see FLUENT-6543).
        if (!this._camera.isPerspective) {
          this._camera.getWorldDirection(this._eye);
        }

        this.gizmo[this._mode].setActivePlane(this.axis, this._eye, this.space === "local");

        var planeIntersect = this._intersectObjects(pointer, [this.gizmo[this._mode].activePlane]);

        if (planeIntersect)
        this._offset.copy(planeIntersect.point);

        this._oldPosition.copy(this.object.position);
        this._oldScale.copy(this.object.scale);

        this._oldRotationMatrix.extractRotation(this.object.matrix);
        this._worldRotationMatrix.extractRotation(this.object.matrixWorld);

        if (this.object.parent) {
          this._parentRotationMatrix.extractRotation(this.object.parent.matrixWorld);
          this._parentScale.setFromMatrixScale(this._tempMatrix.copy(this.object.parent.matrixWorld).invert());
        } else {
          this._parentRotationMatrix.extractRotation(this.object.matrixWorld);
          this._parentScale.setFromMatrixScale(this._tempMatrix.copy(this.object.matrixWorld).invert());
        }

        // show rotation start line and ticks
        if (this._mode === "transrotate" && this.gizmo[this._mode].activeMode === "rotate") {
          {
            const startLinePositions = this.startLine.geometry.getAttribute('position');
            startLinePositions.setXYZ(0, 0, 0, 0);
            startLinePositions.setXYZ(1, 0, 0, 1);
            startLinePositions.applyMatrix4(this.matrixWorld);
            startLinePositions.needsUpdate = true;
            this.parent.add(this.startLine);
          }

          let pos = this.object.geometry.getAttribute('position');
          let pt1 = new THREE.Vector3().fromBufferAttribute(pos, 0).applyMatrix4(this.object.matrixWorld);
          let pt2 = new THREE.Vector3().fromBufferAttribute(pos, 1).applyMatrix4(this.object.matrixWorld);
          let pt3 = new THREE.Vector3().fromBufferAttribute(pos, 2).applyMatrix4(this.object.matrixWorld);
          let pt4 = new THREE.Vector3().fromBufferAttribute(pos, 3).applyMatrix4(this.object.matrixWorld);

          const centerLinePositions = this.centerLine.geometry.getAttribute('position');
          if (this.axis === "RX") {
            pt1.lerp(pt3, 0.5);
            pt2.lerp(pt4, 0.5);
            let dist = pt1.distanceTo(pt2);
            this.centerLine.material.dashSize = dist / 15;
            this.centerLine.material.gapSize = dist / 30;
            centerLinePositions.setXYZ(0, pt1.x, pt1.y, pt1.z);
            centerLinePositions.setXYZ(1, pt2.x, pt2.y, pt2.z);
          } else {
            pt1.lerp(pt2, 0.5);
            pt3.lerp(pt4, 0.5);
            let dist = pt1.distanceTo(pt3);
            this.centerLine.material.dashSize = dist / 15;
            this.centerLine.material.gapSize = dist / 30;
            centerLinePositions.setXYZ(0, pt1.x, pt1.y, pt1.z);
            centerLinePositions.setXYZ(1, pt3.x, pt3.y, pt3.z);
          }
          this.centerLine.computeLineDistances();
          centerLinePositions.needsUpdate = true;
          this.parent.add(this.centerLine);

          this.ticks[this.axis].position.copy(this.position);
          this.ticks[this.axis].quaternion.copy(this.quaternion);
          this.ticks[this.axis].scale.copy(this.scale);
          this.parent.add(this.ticks[this.axis]);
        }

      }

    }

    this._dragging = true;

    return intersect ? true : false;

  }

  onPointerMove(event) {

    if (this.object === undefined || this.axis === null || this._dragging === false) return false;

    var pointer = event;

    var planeIntersect = this._intersectObjects(pointer, [this.gizmo[this._mode].activePlane]);

    if (planeIntersect)
    this._point.copy(planeIntersect.point);

    var mode = this.gizmo[this._mode].activeMode;
    if (mode == "translate") {

      this._point.sub(this._offset);
      this._point.multiply(this._parentScale);

      if (this.space == "local") {

        this._point.applyMatrix4(this._tempMatrix.copy(this._worldRotationMatrix).invert());

        if (this.axis.search("X") == -1) this._point.x = 0;
        if (this.axis.search("Y") == -1) this._point.y = 0;
        if (this.axis.search("Z") == -1) this._point.z = 0;

        this._point.applyMatrix4(this._oldRotationMatrix);

        this.object.position.copy(this._oldPosition);
        this.object.position.add(this._point);

      }

      if (this.space == "world" || this.axis.search("XYZ") != -1) {

        this._projX.copy(this.unitX);
        this._projY.copy(this.unitY);
        this._projZ.copy(this.unitZ);
        this._tempVector.set(0, 0, 0);
        if (this.axis.search("X") != -1) {
          this._projX.multiplyScalar(this._point.dot(this.unitX));
          this._tempVector.add(this._projX);
        }
        if (this.axis.search("Y") != -1) {
          this._projY.multiplyScalar(this._point.dot(this.unitY));
          this._tempVector.add(this._projY);
        }
        if (this.axis.search("Z") != -1) {
          this._projZ.multiplyScalar(this._point.dot(this.unitZ));
          this._tempVector.add(this._projZ);
        }
        this._point.copy(this._tempVector);

        this._point.applyMatrix4(this._tempMatrix.copy(this._parentRotationMatrix).invert());

        this.object.position.copy(this._oldPosition);
        this.object.position.add(this._point);

      }

    } else if (mode == "scale") {

      this._point.sub(this._offset);
      this._point.multiply(this._parentScale);

      if (this.space == "local") {

        if (this.axis == "XYZ") {

          this._scale = 1 + this._point.y / 50;

          this.object.scale.x = this._oldScale.x * this._scale;
          this.object.scale.y = this._oldScale.y * this._scale;
          this.object.scale.z = this._oldScale.z * this._scale;

        } else {

          this._point.applyMatrix4(this._tempMatrix.copy(this._worldRotationMatrix).invert());

          if (this.axis == "X") this.object.scale.x = this._oldScale.x * (1 + this._point.x / 50);
          if (this.axis == "Y") this.object.scale.y = this._oldScale.y * (1 + this._point.y / 50);
          if (this.axis == "Z") this.object.scale.z = this._oldScale.z * (1 + this._point.z / 50);

        }

      }

    } else if (mode == "rotate") {

      this._point.sub(this._worldPosition);
      this._point.multiply(this._parentScale);
      this._tempVector.copy(this._offset).sub(this._worldPosition);
      this._tempVector.multiply(this._parentScale);

      if (this.axis == "RE") {

        this._tempMatrix.copy(this._lookAtMatrix).invert();
        this._point.applyMatrix4(this._tempMatrix);
        this._tempVector.applyMatrix4(this._tempMatrix);

        this._rotation.set(Math.atan2(this._point.z, this._point.y), Math.atan2(this._point.x, this._point.z), Math.atan2(this._point.y, this._point.x));
        this._offsetRotation.set(Math.atan2(this._tempVector.z, this._tempVector.y), Math.atan2(this._tempVector.x, this._tempVector.z), Math.atan2(this._tempVector.y, this._tempVector.x));

        this._tempQuaternion.setFromRotationMatrix(this._tempMatrix.copy(this._parentRotationMatrix).invert());

        var rotz = this._rotation.z - this._offsetRotation.z;
        if (this.snap !== null) {
          let rotsnap = Math.round(rotz / this.snap) * this.snap;
          if (Math.abs(rotsnap - rotz) < this.snapDelta) {
            rotz = rotsnap;
          }
        }
        this._quaternionE.setFromAxisAngle(this._eye, rotz);
        this._quaternionXYZ.setFromRotationMatrix(this._worldRotationMatrix);

        this._tempQuaternion.multiplyQuaternions(this._tempQuaternion, this._quaternionE);
        this._tempQuaternion.multiplyQuaternions(this._tempQuaternion, this._quaternionXYZ);

        this.object.quaternion.copy(this._tempQuaternion);

      } else if (this.axis == "RXYZE") {

        var tempAxis = this._point.clone().cross(this._tempVector).normalize(); // rotation axis

        this._tempQuaternion.setFromRotationMatrix(this._tempMatrix.copy(this._parentRotationMatrix).invert());

        var rot = -this._point.clone().angleTo(this._tempVector);
        if (this.snap !== null) {
          let rotsnap = Math.round(rot / this.snap) * this.snap;
          if (Math.abs(rotsnap - rot) < this.snapDelta) {
            rot = rotsnap;
          }
        }
        this._quaternionX.setFromAxisAngle(tempAxis, rot);
        this._quaternionXYZ.setFromRotationMatrix(this._worldRotationMatrix);

        this._tempQuaternion.multiplyQuaternions(this._tempQuaternion, this._quaternionX);
        this._tempQuaternion.multiplyQuaternions(this._tempQuaternion, this._quaternionXYZ);

        this.object.quaternion.copy(this._tempQuaternion);

      } else if (this.space == "local") {

        this._tempMatrix.copy(this._worldRotationMatrix).invert();
        this._point.applyMatrix4(this._tempMatrix);
        this._tempVector.applyMatrix4(this._tempMatrix);

        let projx = this._point.dot(this.unitX),projy = this._point.dot(this.unitY),projz = this._point.dot(this.unitZ);
        let tempx = this._tempVector.dot(this.unitX),tempy = this._tempVector.dot(this.unitY),tempz = this._tempVector.dot(this.unitZ);
        this._rotation.set(Math.atan2(projz, projy), Math.atan2(projx, projz), Math.atan2(projy, projx));
        this._offsetRotation.set(Math.atan2(tempz, tempy), Math.atan2(tempx, tempz), Math.atan2(tempy, tempx));

        let rotx = this._rotation.x - this._offsetRotation.x;
        let roty = this._rotation.y - this._offsetRotation.y;
        let rotz = this._rotation.z - this._offsetRotation.z;
        if (this.snap !== null) {
          if (this.axis.search("X") != -1) {
            let rotsnap = Math.round(rotx / this.snap) * this.snap;
            if (Math.abs(rotsnap - rotx) < this.snapDelta) {
              rotx = rotsnap;
            }
          }
          if (this.axis.search("Y") != -1) {
            let rotsnap = Math.round(roty / this.snap) * this.snap;
            if (Math.abs(rotsnap - roty) < this.snapDelta) {
              roty = rotsnap;
            }
          }
          if (this.axis.search("Z") != -1) {
            let rotsnap = Math.round(rotz / this.snap) * this.snap;
            if (Math.abs(rotsnap - rotz) < this.snapDelta) {
              rotz = rotsnap;
            }
          }
        }
        this._quaternionX.setFromAxisAngle(this.unitX, rotx);
        this._quaternionY.setFromAxisAngle(this.unitY, roty);
        this._quaternionZ.setFromAxisAngle(this.unitZ, rotz);
        this._quaternionXYZ.setFromRotationMatrix(this._oldRotationMatrix);

        if (this.axis == "RX") this._quaternionXYZ.multiplyQuaternions(this._quaternionXYZ, this._quaternionX);
        if (this.axis == "RY") this._quaternionXYZ.multiplyQuaternions(this._quaternionXYZ, this._quaternionY);
        if (this.axis == "RZ") this._quaternionXYZ.multiplyQuaternions(this._quaternionXYZ, this._quaternionZ);

        this.object.quaternion.copy(this._quaternionXYZ);

      } else if (this.space == "world") {

        let projx = this._point.dot(this.unitX),projy = this._point.dot(this.unitY),projz = this._point.dot(this.unitZ);
        let tempx = this._tempVector.dot(this.unitX),tempy = this._tempVector.dot(this.unitY),tempz = this._tempVector.dot(this.unitZ);
        this._rotation.set(Math.atan2(projz, projy), Math.atan2(projx, projz), Math.atan2(projy, projx));
        this._offsetRotation.set(Math.atan2(tempz, tempy), Math.atan2(tempx, tempz), Math.atan2(tempy, tempx));

        this._tempQuaternion.setFromRotationMatrix(this._tempMatrix.copy(this._parentRotationMatrix).invert());

        let rotx = this._rotation.x - this._offsetRotation.x;
        let roty = this._rotation.y - this._offsetRotation.y;
        let rotz = this._rotation.z - this._offsetRotation.z;
        if (this.snap !== null) {
          if (this.axis.search("X") != -1) {
            let rotsnap = Math.round(rotx / this.snap) * this.snap;
            if (Math.abs(rotsnap - rotx) < this.snapDelta) {
              rotx = rotsnap;
            }
          }
          if (this.axis.search("Y") != -1) {
            let rotsnap = Math.round(roty / this.snap) * this.snap;
            if (Math.abs(rotsnap - roty) < this.snapDelta) {
              roty = rotsnap;
            }
          }
          if (this.axis.search("Z") != -1) {
            let rotsnap = Math.round(rotz / this.snap) * this.snap;
            if (Math.abs(rotsnap - rotz) < this.snapDelta) {
              rotz = rotsnap;
            }
          }
        }
        this._quaternionX.setFromAxisAngle(this.unitX, rotx);
        this._quaternionY.setFromAxisAngle(this.unitY, roty);
        this._quaternionZ.setFromAxisAngle(this.unitZ, rotz);
        this._quaternionXYZ.setFromRotationMatrix(this._worldRotationMatrix);

        if (this.axis == "RX") this._tempQuaternion.multiplyQuaternions(this._tempQuaternion, this._quaternionX);
        if (this.axis == "RY") this._tempQuaternion.multiplyQuaternions(this._tempQuaternion, this._quaternionY);
        if (this.axis == "RZ") this._tempQuaternion.multiplyQuaternions(this._tempQuaternion, this._quaternionZ);

        this._tempQuaternion.multiplyQuaternions(this._tempQuaternion, this._quaternionXYZ);

        this.object.quaternion.copy(this._tempQuaternion);

      }

      // show rotation end line
      if (this._mode === "transrotate") {
        this.add(this.endLine);
        this.add(this.centerMark);
      }

    }

    // update matrix
    this.object.matrixAutoUpdate = true;

    this.update(true);
    this.dispatchEvent(this._changeEvent);
    this.dispatchEvent(this._objectChangeEvent);

    return planeIntersect ? true : false;

  }

  onPointerUp(event) {

    if (this._dragging && this.axis !== null) {
      this._mouseUpEvent.mode = this._mode;
      this.dispatchEvent(this._mouseUpEvent);
    }
    this._dragging = false;

    this.gizmo[this._mode].show();

    this.updateUnitVectors();

    // remove rotation start/end lines
    if (this._mode === "transrotate" && this.gizmo[this._mode].activeMode === "rotate") {
      this.remove(this.endLine);
      this.remove(this.centerMark);
      this.parent.remove(this.centerLine);
      this.parent.remove(this.startLine);
      this.parent.remove(this.ticks[this.axis]);
    }

    return false;

  }

  // Releases GPU-side textures, materials, and geometries created in the constructor. Call when the section tool is
  // deactivated or the viewer is destroyed to avoid leaking WebGL resources. Does not remove this from its parent —
  // the caller is responsible for that.
  dispose() {
    const disposeObject = (object) => {
      object.geometry?.dispose();
      if (object.material) {
        object.material.map?.dispose();
        object.material.dispose();
      }
    };

    if (this._mode !== "transrotate") return;

    disposeObject(this.centerMark);

    for (const key of Object.keys(this.ticks)) {
      this.ticks[key].children.forEach(disposeObject);
    }
  }

  static intersectObjects(clientX, clientY, objects, camera, recursive) {
    // Convert client to viewport coords (in [-1,1]^2)
    let x = clientX / camera.clientWidth * 2 - 1;
    let y = -(clientY / camera.clientHeight) * 2 + 1; // y-direction flips between canvas and viewport coords

    if (camera.isPerspective) {
      _pointerVector.set(x, y, 0.5);
      _pointerVector.unproject(camera);
      _ray.set(camera.position, _pointerVector.sub(camera.position).normalize());
    } else {
      _pointerVector.set(x, y, -1);
      _pointerVector.unproject(camera);
      _pointerDir.set(0, 0, -1);
      _ray.set(_pointerVector, _pointerDir.transformDirection(camera.matrixWorld));
    }

    let intersections = _ray.intersectObjects(objects, recursive);
    return intersections[0] ? intersections[0] : null;
  }
}